/**
 * Shared hook that loads the dropdown data needed by the Employee form:
 *   - manager candidates (super_admin / hr_manager / manager)
 *   - departments hierarchy (flattened for the dropdown)
 *   - branches
 *
 * Returns the lists plus a `createBranch` helper used by the inline
 * "create new branch" widget.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export function useEmployeeFormDropdowns(token) {
  const [managers, setManagers] = useState([]);
  const [departmentsList, setDepartmentsList] = useState([]);
  const [branchesList, setBranchesList] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    const fetchAll = async () => {
      setLoading(true);
      try {
        const [empResp, deptResp, branchResp] = await Promise.allSettled([
          axios.get(`${API_URL}/employees/managers`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_URL}/departments/hierarchy/tree`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_URL}/branches/`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        if (cancelled) return;

        if (empResp.status === 'fulfilled') {
          setManagers(empResp.value.data.managers || []);
        }

        if (deptResp.status === 'fulfilled') {
          const hierarchy = deptResp.value.data.hierarchy || [];
          const flat = [];
          hierarchy.forEach((main) => {
            flat.push({ ...main, isMain: true, level: 0 });
            (main.sub_departments || []).forEach((sub) => {
              flat.push({ ...sub, isMain: false, level: 1, parentName: main.name_en });
            });
          });
          setDepartmentsList(flat);
        } else {
          // Fallback to flat departments
          try {
            const flatResp = await axios.get(`${API_URL}/departments/`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (!cancelled) setDepartmentsList(flatResp.data.departments || []);
          } catch {
            // ignore
          }
        }

        if (branchResp.status === 'fulfilled') {
          setBranchesList(branchResp.value.data.branches || []);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAll();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const createBranch = useCallback(
    async (name) => {
      const trimmed = (name || '').trim();
      if (!trimmed) return null;
      const resp = await axios.post(
        `${API_URL}/branches/`,
        { name_en: trimmed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newBranch = resp.data;
      setBranchesList((prev) => [...prev, newBranch]);
      return newBranch;
    },
    [token]
  );

  return { managers, departmentsList, branchesList, loading, createBranch };
}
